import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ConnectNotificationBanner } from '@stripe/react-connect-js'
import { IconArrowRight, IconReceiptTax, IconX } from '@tabler/icons-react'
import { getStripeConnection, getTaxSettings } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'
import { MascotOutlinePattern } from '../../MascotOutlinePattern'
import { StripeMark } from './StripeMark'
import { StripeEmbedProvider, isEmbeddedStripeConfigured } from './StripeEmbed'
import { StripeTaxDialog } from './StripeTaxSettings'
import { closeNoticeForSession, dismissNotice, isNoticeHidden } from '../../../lib/dismissedNotices'

/**
 * Lo que Stripe espera del vendedor, arriba de la página y con el mismo banner
 * que un traspaso de sitio.
 *
 * Dos avisos, cada uno sólo si hace falta:
 *
 *   · Stripe pide algo —confirmar el correo, un documento, un dato del
 *     negocio—. Lo sabemos por su propio aviso incrustado
 *     (`ConnectNotificationBanner`), que se monta escondido sólo para contar
 *     los pendientes; «Resolver» lo abre en una ventana, que es donde se
 *     completa. Antes ese aviso salía suelto a media pantalla de Ajustes ›
 *     Stripe y en ningún otro sitio.
 *   · La configuración fiscal está a medias. Se ofrece, no se exige: la
 *     obligación fiscal es del negocio —lo dicen los términos—, Stripe Tax es
 *     opcional y hay quien no necesita cobrar impuestos. Por eso se puede
 *     callar para siempre («No volver a mostrar»); el estado sigue en la fila
 *     de impuestos de Tu cuenta, así que la información no desaparece.
 *
 * El de Stripe sólo se cierra por sesión: lo que pide Stripe puede acabar
 * frenando los cobros o los depósitos, y eso no se puede silenciar para
 * siempre.
 *
 * Va en Stripe, en Datos del negocio y en Región y moneda: los tres sitios a
 * los que alguien llega a revisar los datos de su negocio. Sin cuenta de
 * Stripe no hay nada que pedir y no pinta nada.
 */
export function StripeAttentionBanners({ projectId, token }: { projectId: string; token: string }) {
  const { t } = useI18n()
  const [hasAccount, setHasAccount] = useState(false)
  const [pending, setPending] = useState(0)
  const [taxIncomplete, setTaxIncomplete] = useState(false)
  const [open, setOpen] = useState<'stripe' | 'tax' | null>(null)
  const [stripeHidden, setStripeHidden] = useState(() => isNoticeHidden('stripe-attention'))
  const [taxHidden, setTaxHidden] = useState(() => isNoticeHidden('stripe-tax'))
  const showStripe = pending > 0 && !stripeHidden
  const showTax = taxIncomplete && !taxHidden
  const embedded = isEmbeddedStripeConfigured()

  const loadTax = useCallback(async () => {
    try {
      const tax = await getTaxSettings(projectId, token)
      setTaxIncomplete(tax.status !== 'active')
    } catch {
      /* Sin respuesta no se afirma nada: mejor sin aviso que con uno falso. */
    }
  }, [projectId, token])

  useEffect(() => {
    let alive = true
    getStripeConnection(projectId, token)
      .then((connection) => {
        if (!alive || !connection.account) return
        setHasAccount(true)
        void loadTax()
      })
      .catch(() => { /* sin cuenta, sin avisos */ })
    return () => { alive = false }
  }, [projectId, token, loadTax])

  if (!hasAccount || !embedded) return null

  return (
    <>
      {/* El contador: el aviso de Stripe, montado sin verse, sólo para saber
          cuántas cosas pide. No se puede preguntar de otra forma: esa lista la
          tiene Stripe, no nuestra API. */}
      <div hidden>
        <StripeEmbedProvider projectId={projectId} token={token} surface="inline">
          <ConnectNotificationBanner
            collectionOptions={{ fields: 'eventually_due', futureRequirements: 'include' }}
            onNotificationsChange={(response) => setPending(response.actionRequired)}
            onLoadError={() => setPending(0)}
          />
        </StripeEmbedProvider>
      </div>

      {showStripe || showTax ? (
        <div className="dash-invite-banners">
          {showStripe ? (
            <section className="dash-invite-banner dash-loaded-content-in">
              <MascotOutlinePattern className="dash-invite-banner-pattern" eyes={false} />
              <div className="dash-invite-banner-copy">
                <span className="dash-invite-banner-kicker">
                  <StripeMark size={13} color="currentColor" />
                  {t('stripeAttention.kicker')}
                </span>
                <strong>{t('stripeAttention.stripe.title')}</strong>
                <p>{t(pending === 1 ? 'stripeAttention.stripe.bodyOne' : 'stripeAttention.stripe.bodyMany', { count: pending })}</p>
              </div>
              <div className="dash-invite-banner-actions">
                <button
                  type="button"
                  className="dash-invite-banner-btn"
                  onClick={() => { closeNoticeForSession('stripe-attention'); setStripeHidden(true) }}
                >
                  {t('stripeAttention.close')}
                </button>
                <button type="button" className="dash-invite-banner-btn is-primary" onClick={() => setOpen('stripe')}>
                  {t('stripeAttention.stripe.cta')} <IconArrowRight size={14} aria-hidden="true" />
                </button>
              </div>
            </section>
          ) : null}

          {showTax ? (
            <section className="dash-invite-banner dash-loaded-content-in">
              <MascotOutlinePattern className="dash-invite-banner-pattern" eyes={false} />
              <div className="dash-invite-banner-copy">
                <span className="dash-invite-banner-kicker">
                  <IconReceiptTax size={13} aria-hidden="true" />
                  {t('stripeAttention.kicker')}
                </span>
                <strong>{t('stripeAttention.tax.title')}</strong>
                <p>{t('stripeAttention.tax.body')}</p>
              </div>
              <div className="dash-invite-banner-actions">
                <button
                  type="button"
                  className="dash-invite-banner-btn"
                  onClick={() => { dismissNotice('stripe-tax'); setTaxHidden(true) }}
                >
                  {t('stripeAttention.neverShow')}
                </button>
                <button type="button" className="dash-invite-banner-btn is-primary" onClick={() => setOpen('tax')}>
                  {t('stripeAttention.tax.cta')} <IconArrowRight size={14} aria-hidden="true" />
                </button>
              </div>
            </section>
          ) : null}
        </div>
      ) : null}

      {open === 'stripe' ? (
        <StripeNoticeDialog projectId={projectId} token={token} onClose={() => setOpen(null)} />
      ) : null}
      {open === 'tax' ? (
        <StripeTaxDialog
          projectId={projectId}
          token={token}
          onChanged={() => { void loadTax() }}
          onClose={() => { setOpen(null); void loadTax() }}
        />
      ) : null}
    </>
  )
}

/** Lo que Stripe pide, con su formulario para resolverlo, en una ventana. */
function StripeNoticeDialog({ projectId, token, onClose }: { projectId: string; token: string; onClose: () => void }) {
  const { t } = useI18n()

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div
      className="dash-modal-backdrop dash-stripe-embed-backdrop"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <div className="dash-stripe-embed dash-stripe-tax-dialog" role="dialog" aria-modal="true" aria-label={t('stripeAttention.stripe.title')}>
        <button type="button" className="dash-stripe-embed-close" aria-label={t('stripe.embed.close')} onClick={onClose}>
          <IconX size={16} />
        </button>
        <header className="dash-stripe-tax-dialog-head">
          <h2>{t('stripeAttention.stripe.title')}</h2>
          <p>{t('stripeAttention.stripe.dialogBody')}</p>
        </header>
        <StripeEmbedProvider projectId={projectId} token={token}>
          <ConnectNotificationBanner
            collectionOptions={{ fields: 'eventually_due', futureRequirements: 'include' }}
          />
        </StripeEmbedProvider>
      </div>
    </div>,
    document.body,
  )
}
